import { useState, useRef, useEffect } from "react";
import { Globe, FileText, FolderOpen, X } from "lucide-react";
import { TINTA, MARCA, ACCION, MONO } from "./constants.js";
import { reducirImagen, normalizarUrl } from "./lib/util.js";
import { cargarCapturas, guardarCaptura, borrarCaptura } from "./lib/db.js";
import Navegador from "./components/Navegador.jsx";
import Editor from "./components/Editor.jsx";
import Galeria from "./components/Galeria.jsx";
import Informe from "./components/Informe.jsx";
import ErrorBoundary from "./components/ErrorBoundary.jsx";

/* ============================================================
   REVISOR WEB — herramienta personal de revisión visual.
   App orquesta las vistas y conserva el MediaStream persistente de captura.
   ============================================================ */
export default function App() {
  const [vista, setVista] = useState("navegador"); // navegador | editor | galeria | informe
  const [url, setUrl] = useState("");
  const [urlCargada, setUrlCargada] = useState("");
  const [capturas, setCapturas] = useState([]);
  const [imagenParaEditar, setImagenParaEditar] = useState(null);
  const [capturaEnEdicion, setCapturaEnEdicion] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [aviso, setAviso] = useState("");
  const [compartiendo, setCompartiendo] = useState(false);
  const fileRef = useRef(null);
  const visorRef = useRef(null); // área del sitio embebido (lo que se recorta)
  const streamRef = useRef(null); // stream de pantalla persistente

  useEffect(() => {
    cargarCapturas().then((c) => {
      setCapturas(c);
      setCargando(false);
    });
    return () => {
      if (streamRef.current)
        streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  function detenerCompartir() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCompartiendo(false);
  }

  // `destino` opcional: al abrir un marcador, el Navegador pasa su URL directamente
  // (el estado `url` aún no se habría actualizado en ese mismo tick).
  function cargarSitio(destino) {
    const u = normalizarUrl(destino ?? url);
    if (u) {
      setUrlCargada(u);
      setUrl(u);
    }
  }

  async function capturarPantalla() {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      setAviso(
        "Este navegador no permite capturar la pantalla (frecuente en tablets). Toma el pantallazo con tu dispositivo y usa «Subir imagen»."
      );
      return;
    }
    try {
      // 1) Obtener (o reutilizar) el stream de esta pestaña
      let stream = streamRef.current;
      if (!stream || !stream.active) {
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: { displaySurface: "browser" },
          preferCurrentTab: true, // Chrome: sugiere "Esta pestaña"
          selfBrowserSurface: "include",
          audio: false,
        });
        streamRef.current = stream;
        setCompartiendo(true);
        stream.getVideoTracks()[0].addEventListener("ended", () => {
          streamRef.current = null;
          setCompartiendo(false);
        });
      }

      // 2) Tomar un fotograma del video
      const video = document.createElement("video");
      video.srcObject = stream;
      video.muted = true;
      await video.play();
      await new Promise((r) => setTimeout(r, 250));

      // 3) Recortar SOLO el área del visor (el sitio embebido)
      const rect = visorRef.current?.getBoundingClientRect();
      const sX = video.videoWidth / window.innerWidth;
      const sY = video.videoHeight / window.innerHeight;
      const c = document.createElement("canvas");

      if (rect && rect.width > 50 && sX > 0.5 && sX < 4) {
        c.width = Math.round(rect.width * sX);
        c.height = Math.round(rect.height * sY);
        c.getContext("2d").drawImage(
          video,
          Math.round(rect.left * sX),
          Math.round(rect.top * sY),
          c.width,
          c.height,
          0,
          0,
          c.width,
          c.height
        );
      } else {
        // Si compartió otra pestaña/pantalla, se usa el fotograma completo
        c.width = video.videoWidth;
        c.height = video.videoHeight;
        c.getContext("2d").drawImage(video, 0, 0);
      }
      video.pause();

      const data = await reducirImagen(c.toDataURL("image/png"));
      setImagenParaEditar(data);
      setCapturaEnEdicion(null);
      setVista("editor"); // la imagen queda congelada en el área con las herramientas
    } catch (e) {
      setAviso(
        "Captura cancelada o no disponible. Alternativa: toma un pantallazo y usa «Subir imagen»."
      );
    }
  }

  function subirImagen(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const data = await reducirImagen(reader.result);
      setImagenParaEditar(data);
      setCapturaEnEdicion(null);
      setVista("editor");
    };
    reader.readAsDataURL(f);
    e.target.value = "";
  }

  async function alGuardarCaptura(cap) {
    const ids = capturas.map((c) => c.id);
    const ok = await guardarCaptura(cap, ids);
    setCapturas((prev) => {
      const existe = prev.find((c) => c.id === cap.id);
      return existe
        ? prev.map((c) => (c.id === cap.id ? cap : c))
        : [...prev, cap];
    });
    if (!ok)
      setAviso(
        "La captura se guardó en esta sesión, pero el almacenamiento permanente falló."
      );
    setVista("galeria");
  }

  async function eliminar(id) {
    await borrarCaptura(
      id,
      capturas.map((c) => c.id)
    );
    setCapturas((prev) => prev.filter((c) => c.id !== id));
  }

  function editarCaptura(c) {
    setCapturaEnEdicion(c);
    setImagenParaEditar(null);
    setVista("editor");
  }

  const NavBtn = ({ id, icono: Ic, texto, badge }) => (
    <button
      onClick={() => setVista(id)}
      className="relative flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors"
      style={vista === id ? { background: TINTA, color: "#fff" } : { color: TINTA }}
    >
      <Ic size={17} /> <span className="hidden sm:inline">{texto}</span>
      {badge > 0 && (
        <span
          className="min-w-5 h-5 px-1 rounded-full text-xs font-bold flex items-center justify-center text-white"
          style={{ background: MARCA }}
        >
          {badge}
        </span>
      )}
    </button>
  );

  return (
    <div
      className="h-screen flex flex-col"
      style={{ background: "#F3F4F6", color: TINTA, fontFamily: "system-ui, sans-serif" }}
    >
      {/* cabecera */}
      <header
        className="flex items-center gap-2 px-3 sm:px-5 py-2.5 bg-white border-b"
        style={{ borderColor: "#E2E5EB" }}
      >
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
          style={{ background: MARCA }}
        >
          R
        </div>
        <div className="mr-2">
          <p className="font-bold leading-tight text-sm sm:text-base">Revisor Web</p>
          <p
            className="text-[10px] leading-tight tracking-widest"
            style={{ fontFamily: MONO, color: "#6B7280" }}
          >
            REVISIÓN VISUAL
          </p>
        </div>
        <div className="flex-1" />
        <NavBtn id="navegador" icono={Globe} texto="Navegador" />
        <NavBtn id="galeria" icono={FolderOpen} texto="Capturas" badge={capturas.length} />
        <button
          onClick={() => capturas.length && setVista("informe")}
          disabled={!capturas.length}
          className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-40"
          style={{ background: ACCION }}
        >
          <FileText size={17} /> <span className="hidden sm:inline">Informe</span>
        </button>
      </header>

      {aviso && (
        <div
          className="flex items-center gap-2 px-4 py-2 text-sm text-white"
          style={{ background: TINTA }}
        >
          <span className="flex-1">{aviso}</span>
          <button onClick={() => setAviso("")}>
            <X size={16} />
          </button>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={subirImagen}
      />

      {/* ---------- NAVEGADOR ---------- */}
      {vista === "navegador" && (
        <ErrorBoundary nombre="el navegador" alReiniciar={() => setUrlCargada("")}>
          <Navegador
            url={url}
            setUrl={setUrl}
            urlCargada={urlCargada}
            onCargarSitio={cargarSitio}
            onCapturar={capturarPantalla}
            onSubirClick={() => fileRef.current.click()}
            compartiendo={compartiendo}
            onDetenerCompartir={detenerCompartir}
            visorRef={visorRef}
          />
        </ErrorBoundary>
      )}

      {/* ---------- EDITOR ---------- */}
      {vista === "editor" && (imagenParaEditar || capturaEnEdicion) && (
        <div className="flex-1 min-h-0">
          <ErrorBoundary
            nombre="el editor"
            alReiniciar={() => setVista(capturaEnEdicion ? "galeria" : "navegador")}
          >
            <Editor
              imagenBase={imagenParaEditar}
              capturaExistente={capturaEnEdicion}
              urlOrigen={urlCargada}
              onGuardar={alGuardarCaptura}
              onCancelar={() =>
                setVista(capturaEnEdicion ? "galeria" : "navegador")
              }
            />
          </ErrorBoundary>
        </div>
      )}

      {/* ---------- GALERÍA ---------- */}
      {vista === "galeria" && (
        <Galeria
          capturas={capturas}
          cargando={cargando}
          onEditar={editarCaptura}
          onEliminar={eliminar}
          onIrNavegador={() => setVista("navegador")}
        />
      )}

      {/* ---------- INFORME ---------- */}
      {vista === "informe" && (
        <div className="flex-1 min-h-0">
          <Informe capturas={capturas} onVolver={() => setVista("galeria")} />
        </div>
      )}
    </div>
  );
}
